import { NavLink, Outlet } from 'react-router-dom';
import { useQboStatus } from '../api/hooks';
import type { SessionUser } from '../api/types';

function initials(name: string): string {
  const w = name.split(/\s+/).filter(Boolean);
  if (w.length === 0) return '?';
  return (w[0][0] + (w[1]?.[0] ?? '')).toUpperCase();
}

export function Layout({ user }: { user: SessionUser }) {
  const qbo = useQboStatus();
  const isApprover = user.role === 'approver';
  const connected = qbo.data?.connected ?? false;

  return (
    <div className="g-shell">
      <div className="g-topnav-wrap">
        <nav className="g-topnav" aria-label="Primary">
          <NavLink className="g-brand" to="/">
            <span className="g-brand-mark" aria-hidden="true">TQ</span>
            <span className="g-brand-word">
              Tricord <span className="g-brand-accent">Quotes</span>
            </span>
          </NavLink>

          <NavLink className="g-nav-item" to="/create">
            ✨ Create
          </NavLink>
          <NavLink className="g-nav-item" to="/proposal">
            Proposal
          </NavLink>
          <NavLink className="g-nav-item" to="/" end>
            Quotes
          </NavLink>
          <NavLink className="g-nav-item" to="/items" end>
            Items
          </NavLink>
          <NavLink className="g-nav-item" to="/customers" end>
            Customers
          </NavLink>

          <div className="g-drop">
            <button type="button" className="g-nav-item" aria-haspopup="menu">
              + New <span className="g-caret" aria-hidden="true">▾</span>
            </button>
            <div className="g-drop-panel">
              <div className="g-drop-card" role="menu">
                <NavLink role="menuitem" className="g-drop-link" to="/quotes/new">
                  New Quote
                </NavLink>
                <NavLink role="menuitem" className="g-drop-link" to="/items/new">
                  Add Item / Service
                </NavLink>
                <NavLink role="menuitem" className="g-drop-link" to="/customers/new">
                  Add Customer
                </NavLink>
                <NavLink role="menuitem" className="g-drop-link" to="/suppliers/new">
                  Add Supplier
                </NavLink>
              </div>
            </div>
          </div>

          {isApprover && (
            <>
              <span className="g-nav-sep" aria-hidden="true" />
              <NavLink className="g-nav-item" to="/admin/users">
                Admin
              </NavLink>
            </>
          )}

          <span className="g-nav-spacer" />

          {qbo.data && (
            <span
              className="status"
              title="QuickBooks connection"
              style={{ color: connected ? 'var(--good)' : 'var(--bad)', background: 'var(--card-2)', borderColor: 'var(--line)' }}
            >
              QB {connected ? `connected · ${qbo.data.environment}` : qbo.data.status}
            </span>
          )}

          <div className="g-user">
            <span className="g-avatar" aria-hidden="true">
              {initials(user.displayName)}
            </span>
            <span className="g-user-who">
              <span className="g-user-name">{user.displayName}</span>
              <span className="g-user-cap">{user.role}</span>
            </span>
            <a className="g-signout" href="/auth/logout" title="Sign out">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </a>
          </div>
        </nav>
      </div>

      <main className="g-main">
        {!connected && (
          <div className="banner warn">
            <span>
              QuickBooks is not connected{qbo.data?.status === 'reprovision_needed' ? ' (re-authorization required)' : ''}.
              {isApprover ? ' Connect the company to start creating quotes.' : ' Ask an approver to connect QuickBooks.'}
            </span>
            {isApprover && (
              <a className="btn primary" href="/qbo/connect">
                Connect QuickBooks
              </a>
            )}
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
